/**
 * The delay knobs the operator UI reads per departure airport (delay-limits U1) — seam
 * `operator-bff:GET /v1/delay/policy@v1` (B1): the effective `care.delay` knobs of the
 * session's airline at that airport, defaults filled. Read-only here: step 2 of Declare
 * and the delay panel use it to answer «exceeds the limit?» and «hotel?» at once; cases
 * stays authoritative (brief §1b decision 11).
 */
export interface IDelayPolicy {
  /** Longest ETD − STD (hours) this scope accepts as a delay. Integer 1..24 (`CARE_DELAY_LIMITS`). */
  delayLimitHours: number;
  /** The next-day delay hotel rule. */
  nextDayHotel: boolean;
  /** Local hour (departure airport) on the next local day from which the rule gives a hotel. 0..23. */
  nextDayHotelAfterLocalHour: number;
  /** `care.mealThresholdMinutes` — a wait strictly ABOVE it earns the first meal. */
  mealThresholdMinutes: number;
  /** `care.mealVoucherUsd` — one meal tier, per passenger, USD. */
  mealVoucherUsd: number;
  /** `care.delay.mealRepeatEveryMinutes` — a further meal every N minutes; `null` = one meal per delay. */
  mealRepeatEveryMinutes: number | null;
  /** `care.delay.maxMealTiers` — the cap on meals for one delay (first meal + repeats). */
  maxMealTiers: number;
  /**
   * `care.delay.mealTiming` (2026-10-30) — when cases issues a delay meal: `'REAL_WAIT'`
   * (default — nothing before the STD, tier n once the real wait passes its threshold while
   * still delayed), `'AT_STD'` (the covered tiers at the STD, later ones on the real wait) or
   * `'ON_DECLARATION'` (every covered tier at once). Absent from an older BFF → the default.
   */
  mealTiming: 'REAL_WAIT' | 'AT_STD' | 'ON_DECLARATION';
  /** `care.delay.allowForceMealIssue` — a supervisor may give the meals early («Dar la comida ya»). */
  allowForceMealIssue: boolean;
  /** `care.delay.forceMealIssueTiers` — what «Dar la comida ya» gives: every covered meal, or the next one. */
  forceMealIssueTiers: 'ALL_COVERED' | 'NEXT';
  /** `care.delay.issueTierIfDepartedBeforeIt` — covered meals still load when it departs after the STD. */
  issueTierIfDepartedBeforeIt: boolean;
  /** `care.delay.mealIssueLeadMinutes` — meals load this many minutes before their time (never before the STD). 0..120. */
  mealIssueLeadMinutes: number;
  source: 'POLICY' | 'DEFAULTS';
}

/**
 * The documented defaults (policies-tds §23, brief §1b decision 1) — what the BFF answers
 * when no policy says otherwise, and what the UI falls back to when the read fails
 * (invariant 7: policies is fail-soft, a declaration is never blocked by its outage).
 */
export const DELAY_POLICY_DEFAULTS: IDelayPolicy = {
  delayLimitHours: 12,
  nextDayHotel: true,
  nextDayHotelAfterLocalHour: 10,
  // the meal rule cases' delay care runs when the policy sets none (the BFF answers the same)
  mealThresholdMinutes: 120,
  mealVoucherUsd: 25,
  mealRepeatEveryMinutes: null,
  maxMealTiers: 3,
  // policies' default since 2026-10-30 (`care.delay@v3`)
  mealTiming: 'REAL_WAIT',
  allowForceMealIssue: true,
  forceMealIssueTiers: 'ALL_COVERED',
  issueTierIfDepartedBeforeIt: false,
  mealIssueLeadMinutes: 0,
  source: 'DEFAULTS',
};

/**
 * The departure airport a policy is read for — IATA-3, upper-case — or `null` when the
 * code is missing or not one (never a guess: cases then resolves the airline-level knobs,
 * brief §11 "missing origin"). The preview (`useDelayPolicies`) and the declaration body
 * (`flights[i].origin`, `@v2`) share it, so the BFF's limit check reads the airport the
 * screen read.
 */
export function policyAirport(origin: string | null | undefined): string | null {
  const code = origin?.trim().toUpperCase();
  return code && /^[A-Z]{3}$/.test(code) ? code : null;
}

function whole(value: unknown, min: number, max: number, fallback: number): number {
  return typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max ? value : fallback;
}

/** An answer read field by field: a missing or out-of-range knob reads as its default, never as a crash. */
export function parseDelayPolicy(raw: unknown): IDelayPolicy {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const d = DELAY_POLICY_DEFAULTS;
  return {
    delayLimitHours: whole(r.delayLimitHours, 1, 24, d.delayLimitHours),
    nextDayHotel: typeof r.nextDayHotel === 'boolean' ? r.nextDayHotel : d.nextDayHotel,
    nextDayHotelAfterLocalHour: whole(r.nextDayHotelAfterLocalHour, 0, 23, d.nextDayHotelAfterLocalHour),
    mealThresholdMinutes: whole(r.mealThresholdMinutes, 0, 24 * 60, d.mealThresholdMinutes),
    mealVoucherUsd:
      typeof r.mealVoucherUsd === 'number' && Number.isFinite(r.mealVoucherUsd) && r.mealVoucherUsd >= 0
        ? r.mealVoucherUsd
        : d.mealVoucherUsd,
    // absent, null or out of range = one meal per delay
    mealRepeatEveryMinutes: whole(r.mealRepeatEveryMinutes, 60, 1440, 0) || null,
    maxMealTiers: whole(r.maxMealTiers, 1, 24, d.maxMealTiers),
    mealTiming:
      r.mealTiming === 'ON_DECLARATION' || r.mealTiming === 'REAL_WAIT' || r.mealTiming === 'AT_STD'
        ? r.mealTiming
        : d.mealTiming,
    allowForceMealIssue: typeof r.allowForceMealIssue === 'boolean' ? r.allowForceMealIssue : d.allowForceMealIssue,
    forceMealIssueTiers:
      r.forceMealIssueTiers === 'ALL_COVERED' || r.forceMealIssueTiers === 'NEXT'
        ? r.forceMealIssueTiers
        : d.forceMealIssueTiers,
    issueTierIfDepartedBeforeIt:
      typeof r.issueTierIfDepartedBeforeIt === 'boolean' ? r.issueTierIfDepartedBeforeIt : d.issueTierIfDepartedBeforeIt,
    mealIssueLeadMinutes: whole(r.mealIssueLeadMinutes, 0, 120, d.mealIssueLeadMinutes),
    source: r.source === 'POLICY' ? 'POLICY' : 'DEFAULTS',
  };
}
